import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { Popover } from 'antd';
import _ from 'lodash';

import ls from 'local-storage';
import styled from 'styled-components';

import { getFolderContacts, isFolderOfType } from '@libs/NwFolders';
import { showDate, showDateNoTime } from '@libs/NwMoment';

import { useDeleteFolder, useFolders } from '@hooks/API/UseFolders';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import FolderAddModels from '@components/ContactsFolders/FolderAddModels';
import FolderDrawer from '@components/ContactsFolders/FolderDrawer';
import FolderManageDrawer from '@components/ContactsFolders/FolderManageDrawer';
import FolderSelect from '@components/ContactsFolders/FolderSelect';
import ContactsFilterProject from '@components/ContactsSelector/Common/ContactsFilterProject';
import ContactsFiltersSmartListsForm from '@components/ContactsSelector/Common/ContactsFiltersSmartListsForm';
import ContactsSelectedFilters from '@components/ContactsSelector/Common/ContactsSelectedFilters';
import CustomerQuickFilters from '@components/ContactsSelector/CustomersSelector/CustomerQuickFilters';
import ModelQuickFilters from '@components/ContactsSelector/ModelsSelector/ModelQuickFilters';
import ObjectHistory from '@components/CreatedBy/ObjectHistory';
import { MOBILE_FILTERS_NESTED_ZINDEX } from '@components/Filters/FilterForm/FilterFormUI';
import { StyledTabs, StyledSelect, FiltersWrapper } from '@components/Filters/FilterSidebarStyle';
import { NwAddButton, NwEditButton, NwRemoveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import { NwTag } from '@components/Gui/NwTag';

import { useContactsSelector, useContactsSelectorDispatch } from './ContactsSelectorContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;

    .prefix-icon-wrapper {
        position: absolute;
        z-index: 1;
        width: 2.5rem;
        padding-top: 0.5rem;
        padding-bottom: 0.25rem;
        border-right: ${(props) => props.theme.DefaultBorder};
        display: flex;
        align-items: center;
        justify-content: center;
    }

    && .ant-select .ant-select-selector {
        padding-left: calc(3rem);

        .ant-select-selection-search {
            left: 45px;
        }
    }
`;

//i figli di .filters-tab-content sono flex item con align-items flex-start: senza width 100%
//divider e riga azioni si stringono sul contenuto e i bottoni non arrivano a meta' spazio
const FolderSection = styled.div`
    width: 100%;
`;

const FolderInfo = styled.div`
    width: 100%;
    padding: 0 0.25rem;

    > div,
    > p {
        margin: 0 0 1rem;
    }

    //ObjectHistory allinea a destra di suo: qui il blocco segue il resto della colonna
    .folder-history {
        margin-bottom: 2rem;

        > div {
            text-align: left;
        }

        .icon {
            font-size: 1.5rem;
            margin-right: 0.75rem;
        }
    }
`;

const FolderDescription = styled.p`
    font-size: ${(props) => props.theme.FontSizeSm};
    color: ${(props) => props.theme.TextColorMuted};
`;

const FolderInfoRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
`;

const FolderCount = styled.span`
    display: flex;
    align-items: center;
    gap: 0.35rem;
    text-transform: uppercase;
    font-size: ${(props) => props.theme.FontSizeSm};
`;

//i soli scope in cui si sfogliano i contatti: ogni altro selettore serve a sceglierli per qualcos'altro
const WALL_SCOPES = ['models-wall', 'customers-wall', 'mobile'];

//le folder rileggono la privacy da PrivacyLevel: stesse voci di NWPrivacyLevelSelector, come testo
const PRIVACY_LEVEL_LABELS = {
    Public: 'Public',
    NetworkAgency: 'Network Agency',
    Department: 'Department',
    User: 'Personal',
};

const ContactsFiltersSidebar = ({
    itemType,
    projectModels,
    projectShortlists,
    shortlistId,
    selectedGroupName,
    onSelectShortlist,
    customOptionsProp,
    staticOptionsProp,
    selectedItem,
    isMobileDrawer,
}) => {
    const navigate = useNavigate();

    const breakpoints = useNwBreakPoints();

    const { onSelectListType, onSelectActiveSet } = useContactsSelectorDispatch();

    const { selection, global, context, additional } = useContactsSelector();

    const cs_dispatch = useContactsSelectorDispatch();

    const { activeSet, filters, listType } = selection;
    const { sets } = global;

    const workingSetUnsaved = useMemo(() => {
        if (listType === 'smart-lists') {
            const activeSetObj = sets.find((set) => set.id === activeSet);
            return activeSetObj && activeSetObj.unsaved;
        }
        return false;
    }, [activeSet, listType, sets]);

    const { data: folders } = useFolders();
    const { mutateAsync: deleteFolder } = useDeleteFolder();

    const { scope, area } = context;
    const { startOnProjectModels } = additional;

    const [manageFolder, setManageFolder] = useState(false);
    const [addNewFolder, setAddNewFolder] = useState(false);
    const [addModelsToFolder, setAddModelsToFolder] = useState(false);

    const allItemsName = 'all';
    const allItemsLabel = itemType === 'model' ? 'all models' : 'all customers';
    const urlSection = itemType === 'model' ? 'models' : 'customers';

    const enableQuickFilters = area !== 'accounting';
    const enableSmartLists = true;
    const enableFolders = area !== 'scouting' && area !== 'accounting';

    const backToList = () => {
        let urlToNavigate = `/${urlSection}/${listType}/${activeSet}`;
        if (area === 'accounting') {
            urlToNavigate = `/accounting/${urlSection}`;
        }
        navigate(urlToNavigate);
    };

    const goToBooking = () => {
        navigate(`/schedule/${listType}/${activeSet}`);
    };

    const customOptions = useMemo(() => {
        return (
            customOptionsProp ||
            sets
                .filter((s) => !s.fixed)
                .map((set) => {
                    return { value: set.id, label: set.name };
                })
        );
    }, [sets, customOptionsProp]);

    const staticOptions = useMemo(() => {
        return (
            staticOptionsProp ||
            sets
                .filter((s) => s.fixed)
                .map((set) => {
                    return { value: set.id, label: set.name };
                })
        );
    }, [sets, staticOptionsProp]);

    //l'elenco per il select lo costruisce FolderSelect: qui serve solo sapere se ce ne sono
    const typeFolders = useMemo(() => {
        return folders ? folders.filter((folder) => isFolderOfType(folder, itemType)) : [];
    }, [folders, itemType]);

    const onSelectSet = (value) => {
        ls.set(`nw_config_${itemType}swall_listScrollPosition`, 0);
        cs_dispatch({
            type: 'ChangeActiveSet',
            activeSet: value.toString(),
        });
        if (onSelectActiveSet) {
            onSelectActiveSet(value.toString());
        }
    };

    const selectFolder = (folderid, foldername) => {
        cs_dispatch({
            type: 'SelectFolder',
            folderID: folderid,
            folderName: foldername,
        });
    };

    //in tab folders l'activeSet e' l'ID della folder, ma tra un cambio di tab e l'altro puo' valere 'all'
    const selectedFolderID = Number.isNaN(Number(activeSet)) ? undefined : Number(activeSet);

    //il select lavora sugli ID numerici delle folder, il context tiene l'activeSet come stringa
    const handleSelectFolder = (folderID) => {
        const folder = folders.find((item) => item.ID === folderID);
        if (folder) {
            selectFolder(folder.ID.toString(), folder.Name);
        }
    };

    const handleFolderCreated = (folderid, foldername) => {
        cs_dispatch({
            type: 'SelectFolder',
            folderID: folderid.toString(),
            folderName: foldername,
        });
    };

    const handleChangeTab = async (tab) => {
        cs_dispatch({
            type: 'ChangeListType',
            listType: tab,
        });
        if (onSelectListType) {
            onSelectListType(tab);
        }

        setManageFolder(false);

        if (tab === allItemsName) {
            onSelectSet('all');
        }
        if (tab === 'folders') {
            const itemFolders = folders ? folders.filter((folder) => isFolderOfType(folder, itemType)) : [];
            if (itemFolders.length) {
                cs_dispatch({
                    type: 'SelectFolder',
                    folderID: itemFolders[0].ID.toString(),
                    folderName: itemFolders[0].Name,
                });
            }
        }
    };

    // Nel drawer mobile c'è spazio per le label complete, come nella sidebar desktop
    const useFullLabels = isMobileDrawer || breakpoints >= 4;

    let filterOptions = [];
    if (itemType === 'model' && startOnProjectModels && projectModels.length > 0) {
        filterOptions.push({ label: breakpoints < 4 ? 'prj' : 'project', value: 'project' });
    }
    if (area !== 'accounting') {
        filterOptions.push({ label: useFullLabels ? allItemsLabel : 'all', value: allItemsName });
    }
    if (enableSmartLists) {
        filterOptions.push({ label: useFullLabels ? 'search' : 'lists', value: 'smart-lists' });
    }
    if (enableFolders) {
        var folderstab = { label: 'folders', value: 'folders' };
        if (!folders || folders.length === 0) {
            if (selectedItem) {
                folderstab.disabled = true;
            }
        }
        filterOptions.push(folderstab);
    }

    const handleManageFolder = () => {
        setManageFolder(true);
    };

    const handleCloseManageFolder = () => {
        setManageFolder(false);
    };

    const handleAddModelsToFolder = () => {
        setAddModelsToFolder(true);
    };

    const handleCloseAddModelsToFolder = () => {
        setAddModelsToFolder(false);
    };

    const handleAddNewFolder = () => {
        setAddNewFolder(true);
    };

    const handleCloseNewFolder = () => {
        setAddNewFolder(false);
    };

    const handleRemoveFolder = async () => {
        const otherFolders = folders.filter(
            (folder) => isFolderOfType(folder, itemType) && folder.ID !== Number(activeSet)
        );
        if (otherFolders.length) {
            cs_dispatch({
                type: 'SelectFolder',
                folderID: otherFolders[0].ID.toString(),
                folderName: otherFolders[0].Name,
            });
        } else {
            cs_dispatch({
                type: 'ChangeListType',
                listType: allItemsName,
            });
            if (onSelectListType) {
                onSelectListType(allItemsName);
            }
        }

        try {
            await deleteFolder(Number(activeSet));
        } catch (error) {
            console.error('Delete Folder Error => ', error);
        }
    };

    const folder = listType === 'folders' && folders && folders.find((folder) => folder.ID === Number(activeSet));
    const folderItemLabel = itemType === 'model' ? 'model' : 'customer';
    const folderItemsCount = folder ? getFolderContacts(folder).length : 0;
    const leftList = selectedItem || scope === 'booking' ? true : false;
    //le azioni sulle folder hanno senso solo dove si sfogliano i contatti: negli altri selettori
    //(package, event, season, aggiunta a folder, ...) la tab folders serve soltanto a filtrare
    const isWall = WALL_SCOPES.includes(scope);
    // Sotto i 1024px la sidebar è dentro il pannello filtri: i drawer devono stargli sopra.
    const nestedDrawerZIndex = breakpoints < 3 ? MOBILE_FILTERS_NESTED_ZINDEX : undefined;

    return (
        <FiltersWrapper $breakpoints={breakpoints} alignItems={'flex-start'}>
            <StyledTabs
                size="small"
                activeKey={listType}
                onChange={handleChangeTab}
                items={filterOptions.map((tab) => ({
                    label: tab.label,
                    key: tab.value,
                    disabled: tab.disabled,
                    children: (
                        <div className="filters-tab-content">
                            {listType === 'smart-lists' && (
                                <>
                                    <div
                                        className={`filters-tab-content-wrapper${workingSetUnsaved ? ' active-set-changed' : ''}`}
                                    >
                                        {leftList ? (
                                            <SelectWrapper>
                                                <div className="prefix-icon-wrapper">
                                                    <Popover
                                                        placement="rightTop"
                                                        title="Smart List Filters"
                                                        overlayClassName={`nw-popover${workingSetUnsaved ? ' nw-popover-danger' : ''}`}
                                                        content={
                                                            filters.active && filters.active.length > 0 ? (
                                                                <ContactsSelectedFilters />
                                                            ) : (
                                                                <span>no filters applied</span>
                                                            )
                                                        }
                                                        trigger="click"
                                                    >
                                                        {workingSetUnsaved ? (
                                                            <NwIcon
                                                                danger
                                                                pointer
                                                                icon={byPrefixAndName.fass['filters']}
                                                            />
                                                        ) : (
                                                            <NwIcon pointer icon={byPrefixAndName.fasl['filters']} />
                                                        )}
                                                    </Popover>
                                                </div>
                                                <StyledSelect
                                                    autoComplete="off"
                                                    showSearch={true}
                                                    placeholder={'Please select a smart list'}
                                                    dropdownStyle={{ minWidth: breakpoints < 4 ? '250px' : '0' }}
                                                    value={activeSet}
                                                    onChange={onSelectSet}
                                                    options={[
                                                        {
                                                            label: 'Custom Smart Lists',
                                                            options: customOptions,
                                                        },
                                                        {
                                                            label: 'Standard Smart Lists',
                                                            options: staticOptions,
                                                        },
                                                    ]}
                                                />
                                            </SelectWrapper>
                                        ) : (
                                            <StyledSelect
                                                autoComplete="off"
                                                showSearch={true}
                                                placeholder={'Please select a smart list'}
                                                dropdownStyle={{ minWidth: breakpoints < 4 ? '250px' : '0' }}
                                                value={activeSet}
                                                onChange={onSelectSet}
                                                options={[
                                                    {
                                                        label: 'Custom Smart Lists',
                                                        options: customOptions,
                                                    },
                                                    {
                                                        label: 'Standard Smart Lists',
                                                        options: staticOptions,
                                                    },
                                                ]}
                                            />
                                        )}
                                        {workingSetUnsaved && !leftList && (
                                            <span className="unsaved-changes">
                                                <NwIcon icon={byPrefixAndName.fass['triangle-exclamation']} /> unsaved
                                                changes on list
                                            </span>
                                        )}
                                    </div>
                                    {!leftList && <ContactsFiltersSmartListsForm />}
                                </>
                            )}
                            {listType === 'folders' &&
                                (typeFolders.length > 0 ? (
                                    <>
                                        <div className="filters-tab-content-wrapper">
                                            <FolderSelect
                                                type={itemType}
                                                popupClassName="ant-select-dropdown-areaselect"
                                                placeholder="select a folder"
                                                dropdownStyle={{ minWidth: breakpoints < 4 ? '250px' : '0' }}
                                                style={{ marginBottom: '0.5rem' }}
                                                value={selectedFolderID}
                                                onChange={handleSelectFolder}
                                            />
                                        </div>
                                        {!selectedItem && (
                                            <>
                                                {folder && (
                                                    <FolderSection>
                                                        <NwDivider>folder info</NwDivider>
                                                        <FolderInfo>
                                                            {folder.Description && (
                                                                <FolderDescription>
                                                                    {folder.Description}
                                                                </FolderDescription>
                                                            )}
                                                            <FolderInfoRow>
                                                                <FolderCount>
                                                                    <NwTag type="primary">{folderItemsCount}</NwTag>
                                                                    {folderItemLabel}
                                                                    {folderItemsCount === 1 ? '' : 's'}
                                                                </FolderCount>
                                                                <NwTag
                                                                    type={
                                                                        folder.PrivacyLevel === 'User'
                                                                            ? 'danger'
                                                                            : 'primary'
                                                                    }
                                                                >
                                                                    {PRIVACY_LEVEL_LABELS[folder.PrivacyLevel] ||
                                                                        folder.PrivacyLevel}
                                                                </NwTag>
                                                            </FolderInfoRow>
                                                            <div className="folder-history">
                                                                <ObjectHistory object={folder} muted />
                                                            </div>
                                                        </FolderInfo>
                                                        {isWall && (
                                                            <>
                                                                <NwDivider lessMargin>
                                                                    actions on current folder
                                                                </NwDivider>
                                                                <div className="filter-actions">
                                                                    {itemType === 'model' && (
                                                                        <>
                                                                            <NwAddButton
                                                                                size="small"
                                                                                ghost
                                                                                onClick={handleAddModelsToFolder}
                                                                                label="add models"
                                                                            />
                                                                            <NwDividerVertical />
                                                                        </>
                                                                    )}
                                                                    <NwEditButton
                                                                        size="small"
                                                                        ghost
                                                                        onClick={handleManageFolder}
                                                                        label="edit"
                                                                    />
                                                                    <NwDividerVertical />
                                                                    <NwRemoveButton
                                                                        size="small"
                                                                        ghost
                                                                        onClick={handleRemoveFolder}
                                                                        label="delete"
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </FolderSection>
                                                )}
                                                {isWall && (
                                                    <div className="folders-buttons">
                                                        <NwAddButton
                                                            primary
                                                            size="small"
                                                            label="create new folder"
                                                            onClick={handleAddNewFolder}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p>no folders</p>
                                        {isWall && (
                                            <div className="folders-buttons">
                                                <NwAddButton
                                                    primary
                                                    size="small"
                                                    label="create new folder"
                                                    onClick={handleAddNewFolder}
                                                />
                                            </div>
                                        )}
                                    </>
                                ))}
                            {!leftList &&
                                listType === allItemsName &&
                                enableQuickFilters &&
                                (itemType === 'model' ? (
                                    <ModelQuickFilters />
                                ) : (
                                    <CustomerQuickFilters />
                                ))}
                            {listType === 'project' && (
                                <ContactsFilterProject
                                    shortlistId={shortlistId}
                                    selectedGroupName={selectedGroupName}
                                    onSelectShortlist={onSelectShortlist}
                                    projectShortlists={projectShortlists}
                                />
                            )}
                        </div>
                    ),
                }))}
            />
            {(selectedItem || scope === 'booking') && (
                <span className="back-to-list">
                    <NwIcon icon={byPrefixAndName.fass['grid']} onClick={backToList} />
                </span>
            )}
            {!selectedItem && !isMobileDrawer && scope === 'models-wall' && area === 'booking' && (
                <span className="go-to-booking">
                    <NwIcon icon={byPrefixAndName.fasl['calendar']} onClick={goToBooking} />
                </span>
            )}
            {manageFolder && folder && (
                <FolderManageDrawer folder={folder} onClose={handleCloseManageFolder} zIndex={nestedDrawerZIndex} />
            )}
            {addModelsToFolder && folder && (
                <NwDrawer
                    padding="0px"
                    width="80vw"
                    title={`Add models to "${folder.Name}"`}
                    onClose={handleCloseAddModelsToFolder}
                    zIndex={nestedDrawerZIndex}
                >
                    <FolderAddModels folderID={folder.ID} />
                </NwDrawer>
            )}
            {addNewFolder && (
                <FolderDrawer
                    type={itemType}
                    onClose={handleCloseNewFolder}
                    onCreated={handleFolderCreated}
                    zIndex={nestedDrawerZIndex}
                />
            )}
        </FiltersWrapper>
    );
};

export default ContactsFiltersSidebar;
